<template>
  <div class="layout">
    <Sidebar @toggle-sidebar="toggleSidebar" />
    <div class="main-content" :class="{ 'sidebar-collapsed': isSidebarCollapsed }">
      <div class="create-section-container">
        <div class="page-header">
          <h1>Create Homepage Section</h1>
          <button class="back-button" @click="goBack">
            Back to Homepage
          </button>
        </div>

        <form @submit.prevent="handleSubmit" class="section-form">
          <div class="form-group">
            <label for="title">Title</label>
            <input 
              id="title"
              v-model="formData.title"
              type="text"
              required
              placeholder="e.g., Featured Courses"
            >
          </div>

          <div class="form-group">
            <label for="title_ar">Title (Arabic)</label>
            <input 
              id="title_ar"
              v-model="formData.title_ar"
              type="text"
              required
              placeholder="e.g., موجز الدورات المميزة"
              class="rtl-input"
              dir="rtl"
            >
          </div>

          <div class="form-group">
            <label for="subtitle">Subtitle</label>
            <input 
              id="subtitle"
              v-model="formData.subtitle"
              type="text"
              placeholder="e.g., Hand-picked courses for you"
            >
          </div>

          <div class="form-group">
            <label for="subtitle_ar">Subtitle (Arabic)</label>
            <input 
              id="subtitle_ar"
              v-model="formData.subtitle_ar"
              type="text"
              placeholder="e.g., موجز الدورات المميزة"
              class="rtl-input"
              dir="rtl"
            >
          </div>

          <div class="form-group">
            <label for="type">Section Type</label>
            <select id="type" v-model="formData.type" required @change="handleTypeChange">
              <option value="Banner">Banner</option>
              <option value="FullCardCarousel">Full Card Carousel</option>
              <option value="SquareCardCarouselV1">Square Card Carousel V1</option>
              <option value="SquareCardCarouselV2">Square Card Carousel V2</option>
              <option value="TallCardCarousel">Tall Card Carousel</option>
              <option value="GridCarousel">Grid Carousel</option>
            </select>
          </div>


          <div class="form-group">
            <label class="checkbox-label">
              <input 
                type="checkbox"
                v-model="formData.is_active"
              >
              Active Section
            </label>
          </div>

          <div class="form-actions">
            <button type="submit" class="submit-button" :disabled="isSubmitting">
              {{ isSubmitting ? 'Creating...' : 'Create Section' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';
import Sidebar from '@/components/Sidebar.vue';

export default {
  name: 'CreateHomepageSection',
  components: {
    Sidebar
  },
  setup() {
    const router = useRouter();
    const isSubmitting = ref(false);
    const isSidebarCollapsed = ref(false);

    const formData = reactive({
      title: '',
      title_ar: '',
      title_key: '',
      title_key_ar: '',
      subtitle: '',
      subtitle_ar: '',
      type: '',
      ttl: 3600,
      order: 10,
      is_active: true
    });

    const config = reactive({
      max_items: 6,
      show_ratings: false,
      show_progress: false,
      show_duration: false,
      show_subtitle: false,
      show_icon: false,
      columns: 3,
      auto_scroll: false,
      height: 400,
      show_overlay: false
    });

    const handleTypeChange = () => {
      // Reset config when type changes
      Object.keys(config).forEach(key => {
        if (typeof config[key] === 'boolean') {
          config[key] = false;
        } else if (typeof config[key] === 'number') {
          config[key] = key === 'max_items' ? 6 : 
                       key === 'columns' ? 3 : 
                       key === 'height' ? 400 : 0;
        }
      });
    };

    const validateForm = () => {
      if (!formData.title || !formData.title_ar) {
        alert('Both English and Arabic titles are required');
        return false;
      }
      if (!formData.type) {
        alert('Section type is required');
        return false;
      }
      return true;
    };

    const updateTitleKeys = () => {
      formData.title_key = formData.title
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, '');
      
      formData.title_key_ar = formData.title_ar
        .replace(/\s+/g, '_')
        .replace(/[^\u0600-\u06FF0-9_]/g, '');
    };

    const handleSubmit = async () => {
      if (!validateForm()) return;
      
      // Update title keys before submission
      updateTitleKeys();
      
      isSubmitting.value = true;
      try {
        const token = localStorage.getItem('token');
        
        // Create additional_config based on section type
        const additional_config = {};
        
        // Add common config
        if (config.max_items) {
          additional_config.max_items = config.max_items;
        }

        // Add type-specific config
        switch (formData.type) {
          case 'FullCardCarousel':
            if (config.show_ratings) additional_config.show_ratings = true;
            if (config.show_progress) additional_config.show_progress = true;
            if (config.show_duration) additional_config.show_duration = true;
            break;
          case 'SquareCardCarouselV1':
          case 'SquareCardCarouselV2':
            if (config.show_subtitle) additional_config.show_subtitle = true;
            if (config.show_icon) additional_config.show_icon = true;
            break;
          case 'GridCarousel':
            additional_config.columns = config.columns;
            if (config.auto_scroll) additional_config.auto_scroll = true;
            break;
          case 'Banner':
            additional_config.height = config.height;
            if (config.show_overlay) additional_config.show_overlay = true;
            break;
        }

        const payload = {
          ...formData,
          additional_config
        };

        console.log('Sending payload:', payload);

        const response = await axios.post('https://api.kewaar.com/api/admin/homepage-sections', payload, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        console.log('Response:', response.data);
        router.push('/homepage');
      } catch (error) {
        console.error('Error creating section:', error);
        let errorMessage = 'Failed to create section. ';
        
        if (error.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          console.error('Error response:', error.response.data);
          errorMessage += error.response.data.message || 
                         Object.values(error.response.data.errors || {}).flat().join(', ') ||
                         `Server responded with status ${error.response.status}`;
        } else if (error.request) {
          // The request was made but no response was received
          errorMessage += 'No response received from server. Please check your connection.';
        } else {
          // Something happened in setting up the request that triggered an Error
          errorMessage += error.message || 'An unexpected error occurred.';
        }
        
        alert(errorMessage);
      } finally {
        isSubmitting.value = false;
      }
    };

    const goBack = () => {
      router.push('/homepage');
    };

    const toggleSidebar = () => {
      isSidebarCollapsed.value = !isSidebarCollapsed.value;
    };

    return {
      formData,
      config,
      isSubmitting,
      isSidebarCollapsed,
      handleSubmit,
      handleTypeChange,
      goBack,
      toggleSidebar
    };
  }
};
</script>

<style scoped>
.layout {
  display: flex;
  min-height: 100vh;
}

.main-content {
  flex: 1;
  margin-left: 280px;
  transition: margin-left 0.3s ease;
}

.main-content.sidebar-collapsed {
  margin-left: 0;
}

.create-section-container {
  padding: 2rem;
  max-width: 800px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.back-button {
  background: #64748b;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.back-button:hover {
  background: #475569;
}

.section-form {
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.form-group {
  margin-bottom: 1.5rem;
}

label {
  display: block;
  margin-bottom: 0.5rem;
  color: #1e293b;
  font-weight: 500;
}

input[type="text"],
input[type="number"],
select,
textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.2s ease;
}

input[type="text"]:focus,
input[type="number"]:focus,
select:focus,
textarea:focus {
  outline: none;
  border-color: #3b82f6;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

input[type="checkbox"] {
  width: 1.2rem;
  height: 1.2rem;
  cursor: pointer;
}

.error-message {
  display: block;
  color: #dc2626;
  font-size: 0.875rem;
  margin-top: 0.5rem;
}

.form-actions {
  margin-top: 2rem;
  display: flex;
  justify-content: flex-end;
}

.submit-button {
  background: #3b82f6;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
  min-width: 150px;
}

.submit-button:hover:not(:disabled) {
  background: #2563eb;
}

.submit-button:disabled {
  background: #94a3b8;
  cursor: not-allowed;
}

.config-section {
  background: #f8fafc;
  padding: 1.5rem;
  border-radius: 8px;
  margin-top: 1rem;
}

.config-section h3 {
  margin: 0 0 1rem;
  color: #1e293b;
  font-size: 1.1rem;
}

.config-group {
  margin-bottom: 1rem;
  padding: 0.5rem 0;
}

.config-group:last-child {
  margin-bottom: 0;
}

.config-group label {
  font-weight: normal;
}

@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
  }
  
  .create-section-container {
    padding: 1rem;
  }
  
  .section-form {
    padding: 1.5rem;
  }
}

.rtl-input {
  text-align: right;
  direction: rtl;
}

.rtl-input::placeholder {
  direction: rtl;
}
</style> 